import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import CowInvestmentDetail from './CowInvestmentDetail';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const InvestmentDetail = ({ onBack, activeBottomTab, setActiveBottomTab }) => {
  const [showCowDetail, setShowCowDetail] = useState(false);
  const [selectedInvestasiId, setSelectedInvestasiId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState({
    totalInvestment: 'Rp 0',
    totalCount: '0',
    projectedProfit: 'Rp 0',
    bonus: 'Rp 0'
  });
  const [investmentData, setInvestmentData] = useState([]);

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value === 'string') {
      return value;
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch (error) {
      return dateString;
    }
  };

  // Fetch data investasi dari API
  useEffect(() => {
    const fetchInvestasiData = async () => {
      try {
        setLoading(true);
        
        // Get user_id dari localStorage
        let userId = null;
        const savedUserData = localStorage.getItem('user_data');
        if (savedUserData) {
          const parsed = JSON.parse(savedUserData);
          userId = parsed.id;
        }

        if (!userId) {
          setSummaryData({
            totalInvestment: 'Rp 0',
            totalCount: '0',
            projectedProfit: 'Rp 0',
            bonus: 'Rp 0'
          });
          setInvestmentData([]);
          setLoading(false);
          return;
        }

        // Fetch investasi dari endpoint /api/investasi/user/{user_id}
        const investasiResponse = await apiClient.get(API_ENDPOINTS.INVESTASI.BY_USER(userId));

        // Handle response structure investasi
        let investasiData = [];
        if (investasiResponse.data && investasiResponse.data.investasis && Array.isArray(investasiResponse.data.investasis)) {
          investasiData = investasiResponse.data.investasis;
        } else if (investasiResponse.data && Array.isArray(investasiResponse.data)) {
          investasiData = investasiResponse.data;
        } else if (Array.isArray(investasiResponse)) {
          investasiData = investasiResponse;
        }

        // Filter hanya investasi aktif (deleted_at === null)
        const investasiAktif = investasiData.filter(item => item.deleted_at === null);

        // Hitung jumlah investasi (total invoice)
        const totalCount = investasiAktif.length;

        // Fetch investasi-produk untuk setiap investasi dari endpoint /api/investasi-produk/investasi/{investasi_id}
        let allInvestasiProdukData = [];
        const investasiProdukMap = {}; // Map untuk menyimpan investasi-produk per investasi
        
        if (investasiAktif.length > 0) {
          try {
            const investasiProdukPromises = investasiAktif.map(async (investasi) => {
              try {
                // Ambil ID investasi - bisa berupa id atau investasi_id
                const investasiId = investasi.id || investasi.investasi_id;
                if (!investasiId) {
                  console.warn('Investasi tidak memiliki ID:', investasi);
                  return { investasiId: null, data: [] };
                }
                
                const investasiProdukResponse = await apiClient.get(API_ENDPOINTS.INVESTASI_PRODUK.BY_INVESTASI(investasiId));

                // Handle response structure investasi-produk
                let investasiProdukData = [];
                if (investasiProdukResponse.data && investasiProdukResponse.data.investasi_produks && Array.isArray(investasiProdukResponse.data.investasi_produks)) {
                  investasiProdukData = investasiProdukResponse.data.investasi_produks;
                } else if (investasiProdukResponse.data && Array.isArray(investasiProdukResponse.data)) {
                  investasiProdukData = investasiProdukResponse.data;
                } else if (Array.isArray(investasiProdukResponse)) {
                  investasiProdukData = investasiProdukResponse;
                }

                // Simpan ke map
                investasiProdukMap[investasiId] = investasiProdukData;

                return { investasiId, data: investasiProdukData };
              } catch (error) {
                console.error(`Error fetching investasi-produk untuk investasi ${investasi.id}:`, error);
                const investasiId = investasi.id || investasi.investasi_id;
                if (investasiId) {
                  investasiProdukMap[investasiId] = [];
                }
                return { investasiId: investasi.id || investasi.investasi_id, data: [] };
              }
            });

            const allInvestasiProdukResults = await Promise.all(investasiProdukPromises);
            allInvestasiProdukData = allInvestasiProdukResults.flatMap(result => result.data);
          } catch (error) {
            console.error('Error fetching investasi-produk:', error);
          }
        }

        // Fetch harga produk untuk setiap investasi-produk yang punya produk_id
        let totalInvestasi = 0;
        const produkIds = allInvestasiProdukData
          .map(item => item.produk_id)
          .filter(id => id !== null && id !== undefined);

        let produkMap = {};
        if (produkIds.length > 0) {
          try {
            const uniqueProdukIds = [...new Set(produkIds)];
            const produkPromises = uniqueProdukIds.map(async (produkId) => {
              try {
                const produkResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_DETAIL(produkId));
                
                // Handle response structure
                let produk = null;
                if (produkResponse.data && produkResponse.data.produk) {
                  produk = produkResponse.data.produk;
                } else if (produkResponse.data && !produkResponse.data.produk) {
                  produk = produkResponse.data;
                } else if (!produkResponse.data) {
                  produk = produkResponse;
                }
                
                return {
                  produkId,
                  produkData: produk
                };
              } catch (error) {
                console.error(`Error fetching produk ${produkId}:`, error);
                return {
                  produkId,
                  produkData: null
                };
              }
            });

            const produkResults = await Promise.all(produkPromises);
            
            // Map produk_id ke produk data
            produkResults.forEach(item => {
              produkMap[item.produkId] = item.produkData;
            });

            // Hitung total investasi dari semua investasi-produk berdasarkan harga produk
            totalInvestasi = allInvestasiProdukData.reduce((sum, investasiProduk) => {
              if (investasiProduk.produk_id && produkMap[investasiProduk.produk_id]) {
                const produk = produkMap[investasiProduk.produk_id];
                const harga = produk.harga_beli || produk.price || produk.harga || 0;
                return sum + (typeof harga === 'number' ? harga : parseFloat(harga) || 0);
              }
              return sum;
            }, 0);
          } catch (error) {
            console.error('Error fetching produk prices:', error);
          }
        }

        // Transform investasi data untuk ditampilkan
        const transformedInvestasiData = investasiAktif.map((investasi) => {
          const investasiId = investasi.id || investasi.investasi_id;
          
          // Get bank from investasi data (bisa dari field bank, rekening, atau default)
          const bankName = investasi.bank || investasi.rekening || investasi.bank_name || 'BCA';
          const bankDisplay = bankName.includes('BCA') ? 'Ke Rekening BCA' : 
                            bankName.includes('BNI') ? 'Ke Rekening BNI' :
                            bankName.includes('BRI') ? 'Ke Rekening BRI' :
                            bankName.includes('Mandiri') ? 'Ke Rekening Mandiri' :
                            `Ke Rekening ${bankName}`;
          
          // Get status (default 'Diterima' jika tidak ada field status)
          const status = investasi.status || investasi.status_pembayaran || 'Diterima';
          
          // Get amount - hitung dari investasi-produk terkait
          let amount = 0;
          const relatedInvestasiProduks = investasiProdukMap[investasiId] || [];
          amount = relatedInvestasiProduks.reduce((sum, ip) => {
            if (ip.produk_id && produkMap[ip.produk_id]) {
              const produk = produkMap[ip.produk_id];
              const harga = produk.harga_beli || produk.price || produk.harga || 0;
              return sum + (typeof harga === 'number' ? harga : parseFloat(harga) || 0);
            }
            return sum;
          }, 0);
          
          // Jika masih 0, coba ambil dari field investasi
          if (amount === 0) {
            amount = investasi.nominal || investasi.amount || investasi.total || 0;
          }
          
          // Format date
          const date = formatDate(investasi.created_at || investasi.tanggal || investasi.date);

          return {
            id: investasi.id || investasi.investasi_id || investasi.kode || '-',
            date: date,
            bank: bankDisplay,
            status: status,
            amount: formatCurrency(amount),
            rawData: investasi
          };
        }).sort((a, b) => {
          // Sort by date descending (newest first)
          const dateA = new Date(a.rawData.created_at || a.rawData.tanggal || a.rawData.date);
          const dateB = new Date(b.rawData.created_at || b.rawData.tanggal || b.rawData.date);
          return dateB - dateA;
        });

        // Calculate projected profit (default 10% dari total investasi atau dari API jika ada)
        const projectedProfit = totalInvestasi * 0.1; // 10% default

        // Update summary data
        setSummaryData({
          totalInvestment: formatCurrency(totalInvestasi),
          totalCount: totalCount.toString(),
          projectedProfit: formatCurrency(projectedProfit),
          bonus: 'Rp 0' // Default, bisa diambil dari API jika ada field bonus
        });

        setInvestmentData(transformedInvestasiData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching investasi data:', error);
        setSummaryData({
          totalInvestment: 'Rp 0',
          totalCount: '0',
          projectedProfit: 'Rp 0',
          bonus: 'Rp 0'
        });
        setInvestmentData([]);
        setLoading(false);
      }
    };

    fetchInvestasiData();
  }, []);

  // Jika sedang menampilkan detail sapi
  if (showCowDetail && selectedInvestasiId) {
    return (
      <CowInvestmentDetail 
        investasiId={selectedInvestasiId}
        onBack={() => {
          setShowCowDetail(false);
          setSelectedInvestasiId(null);
        }}
        activeBottomTab={activeBottomTab}
        setActiveBottomTab={setActiveBottomTab}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Wrapper */}
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={onBack}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
              </button>
              <div className="flex items-center space-x-2">
                <Logo size="small" showText={false} />
                <span className="font-bold text-primary text-lg">Kemitraan Ternak</span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-32 bg-gray-50">
          {/* Summary Cards */}
          <section className="px-4 pt-6">
            {/* Jumlah Kemitraan Ternak - Large Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-secondary p-3 rounded-xl">
                    <i className="fa-solid fa-chart-line text-accent text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-gray-600 text-sm font-medium">Jumlah Kemitraan Ternak</h3>
                    <p className="text-2xl font-bold text-primary mt-1">{summaryData.totalInvestment}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Total dan Proyeksi Untung - Small Cards */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-2xl shadow-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-secondary p-2 rounded-lg">
                    <i className="fa-solid fa-chart-line text-accent text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-gray-600 text-xs font-medium">Total</h4>
                    <p className="text-xl font-bold text-primary mt-1">{summaryData.totalCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-secondary p-2 rounded-lg">
                    <i className="fa-solid fa-chart-line text-accent text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-gray-600 text-xs font-medium">Proyeksi Untung</h4>
                    <p className="text-xl font-bold text-primary mt-1">{summaryData.projectedProfit}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bonus - Large Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-secondary p-3 rounded-xl">
                    <i className="fa-solid fa-chart-line text-accent text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-gray-600 text-sm font-medium">Bonus</h3>
                    <p className="text-2xl font-bold text-primary mt-1">{summaryData.bonus}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Kemitraan Ternak */}
          <section className="px-4">
            <h3 className="text-gray-700 font-semibold mb-4">Data Kemitraan Ternak</h3>
            
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg p-4 animate-pulse">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="bg-gray-200 h-4 w-32 rounded mb-2"></div>
                        <div className="bg-gray-200 h-3 w-24 rounded mb-1"></div>
                        <div className="bg-gray-200 h-3 w-28 rounded"></div>
                      </div>
                      <div className="text-right">
                        <div className="bg-gray-200 h-6 w-16 rounded-full mb-2"></div>
                        <div className="bg-gray-200 h-5 w-24 rounded"></div>
                      </div>
                    </div>
                    <div className="bg-gray-200 h-10 w-full rounded-xl"></div>
                  </div>
                ))}
              </div>
            ) : investmentData.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <i className="fa-solid fa-chart-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Belum ada investasi</p>
              </div>
            ) : (
              <div className="space-y-3">
                {investmentData.map((investment, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-secondary font-semibold text-sm">{investment.id}</p>
                      <p className="text-gray-600 text-xs mt-1">{investment.date}</p>
                      <p className="text-gray-600 text-xs">{investment.bank}</p>
                    </div>
                    <div className="text-right">
                      <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                        {investment.status}
                      </span>
                      <p className="text-primary font-bold text-lg mt-2">{investment.amount}</p>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => {
                      setSelectedInvestasiId(investment.rawData.id || investment.rawData.investasi_id || investment.id);
                      setShowCowDetail(true);
                    }}
                    className="w-full bg-accent text-primary font-semibold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 hover:bg-accent/90 transition-colors"
                  >
                    <i className="fa-solid fa-info-circle"></i>
                    <span>Detail Investasi</span>
                  </button>
                </div>
              ))}
              </div>
            )}
          </section>
          
          {/* Extra spacing for bottom navigation */}
          <div className="h-8"></div>
        </main>

        {/* Bottom Navigation */}
        <footer className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-white pt-2">
          <div className="bg-primary mx-4 mb-4 rounded-2xl shadow-lg">
            <div className="flex justify-around items-center h-20 text-white">
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'home' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('home');
                  onBack();
                }}
              >
                <i className="fa-solid fa-house text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Beranda</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'savings' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('savings');
                  onBack();
                }}
              >
                <i className="fa-solid fa-piggy-bank text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Tabungan</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'products' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('products');
                  onBack();
                }}
              >
                <i className="fa-solid fa-box-archive text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Produk</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'investment' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => setActiveBottomTab('investment')}
              >
                <i className="fa-solid fa-arrow-trend-up text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Investasi</span>
              </span>
              <span 
                className={`text-center flex flex-col items-center cursor-pointer ${activeBottomTab === 'account' ? 'text-accent' : 'opacity-70 hover:opacity-100'} transition`}
                onClick={() => {
                  setActiveBottomTab('account');
                  onBack();
                }}
              >
                <i className="fa-solid fa-user-circle text-2xl"></i>
                <span className="text-xs font-semibold mt-1">Akun</span>
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default InvestmentDetail;
